<template>
  <f7-page :page-content="true" name="puestostrabajo" @page:beforein="entrar">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('puestostrabajo.titulo') }}</f7-nav-title>
      <f7-nav-right>
        <f7-button icon-f7="arrow_clockwise" :text="$t('puestostrabajo.recargar')" class="display-flex" @click="cargar"></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('puestostrabajo.noAutorizado') }}</p>
    </f7-block>

    <template v-else>
      <!-- Alta en línea, encima de la tabla, como en la pantalla de la #50. La misma tarjeta
           sirve para editar: lo único que cambia es si hay id o no, y el botón lo dice. -->
      <f7-card class="padding-vertical-half padding-right bloque">
        <f7-card-content>
          <!-- `key` distinto en cada apertura: `rForm` copia `initialData` al crear `formData` y
               no vuelve a mirarlo, así que sin esto pasar de editar un puesto a dar de alta otro
               guardaría los datos del anterior. -->
          <r-form ref="formPuesto" :key="formKey" v-slot="{ rform }" :initialData="editando">
            <f7-row no-gap class="list list-form no-hairlines">
              <f7-col width="100" medium="20">
                <ul>
                  <!-- `all-mayus`: el backend guarda el código en mayúsculas, así que se
                       escribe ya como se va a quedar y no sorprende al guardar. -->
                  <r-input
                    outline
                    floating-label
                    all-mayus
                    :label="$t('puestostrabajo.codigo')"
                    :form="rform"
                    type="text"
                    name="codigo"
                    :readonly="!!editando.id"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="15">
                <ul>
                  <!-- La abreviatura SÍ se edita, al contrario que el código: es nueva (#483) y
                       los puestos que ya existían pueden no tenerla, así que sin poder ponérsela
                       no habría forma de que numeraran sus funciones. -->
                  <r-input
                    outline
                    floating-label
                    all-mayus
                    :label="$t('puestostrabajo.abreviatura')"
                    :form="rform"
                    type="text"
                    name="abreviatura"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="40">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('puestostrabajo.nombre')"
                    :form="rform"
                    type="text"
                    name="nombre"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="25" class="display-flex justify-content-center align-items-center">
                <f7-button fill icon-f7="plus" :text="editando.id ? $t('puestostrabajo.guardar') : $t('puestostrabajo.nuevo')" class="display-flex" @click="guardar"></f7-button>
                <f7-button v-if="editando.id" :text="$t('puestostrabajo.cancelar')" class="display-flex margin-left-half" @click="nuevo"></f7-button>
              </f7-col>
            </f7-row>
          </r-form>
          <!-- Al editar, el código sale bloqueado: no se cambia después del alta, porque lo
               guarda el rastro de rh_audit. Se dice aquí para que no parezca que está roto. -->
          <p class="pie">{{ editando.id ? $t('puestostrabajo.pieCodigoFijo') : $t('puestostrabajo.pieEjemplos') }}</p>
          <!-- Qué es la abreviatura y qué pasa al cambiarla, junto al campo: es lo único nuevo de
               esta pantalla y es lo que decide el código de las funciones del puesto. -->
          <p class="pie">{{ $t('puestostrabajo.pieAbreviatura') }}</p>
        </f7-card-content>
      </f7-card>

      <f7-block strong class="tabla-wrapper">
        <table class="tabla-puestos">
          <thead>
            <tr>
              <th>{{ $t('puestostrabajo.codigo') }}</th>
              <th>{{ $t('puestostrabajo.abreviatura') }}</th>
              <th>{{ $t('puestostrabajo.nombre') }}</th>
              <th class="col-centro">{{ $t('puestostrabajo.funciones') }}</th>
              <th class="col-centro">{{ $t('puestostrabajo.personas') }}</th>
              <th>{{ $t('puestostrabajo.estado') }}</th>
              <th>{{ $t('puestostrabajo.acciones') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="puesto in puestos" :key="puesto.id" :class="{ inactivo: !puesto.activo }">
              <td class="col-codigo">{{ puesto.codigo }}</td>
              <td class="col-codigo">
                <span v-if="puesto.abreviatura">{{ puesto.abreviatura }}</span>
                <span v-else class="sin-abreviatura">{{ $t('puestostrabajo.sinAbreviatura') }}</span>
              </td>
              <td>{{ puesto.nombre }}</td>
              <td class="col-centro">{{ puesto.numeroFunciones }}</td>
              <td class="col-centro">{{ puesto.numeroPersonas }}</td>
              <td>
                <span class="etiqueta-estado" :class="puesto.activo ? 'estado-activo' : 'estado-inactivo'">
                  {{ puesto.activo ? $t('puestostrabajo.activo') : $t('puestostrabajo.inactivo') }}
                </span>
              </td>
              <td class="col-acciones">
                <a href="#" @click.prevent="editar(puesto)">{{ $t('puestostrabajo.editar') }}</a>
                <a v-if="puesto.activo" href="#" @click.prevent="cambiarActivo(puesto, false)">{{ $t('puestostrabajo.desactivar') }}</a>
                <a v-else href="#" @click.prevent="cambiarActivo(puesto, true)">{{ $t('puestostrabajo.reactivar') }}</a>
              </td>
            </tr>
            <tr v-if="!puestos.length">
              <td colspan="7" class="vacio">{{ $t('puestostrabajo.sinResultados') }}</td>
            </tr>
          </tbody>
        </table>
      </f7-block>

      <p class="pie">{{ $t('puestostrabajo.pie') }}</p>
    </template>
  </f7-page>
</template>
<style scoped>
.bloque .pie,
.pie { font-size: 12px; color: #8e8e93; margin: 0 16px 8px; }
.pie.aviso { color: #b26a00; }
.tabla-wrapper { overflow-x: auto; }
.tabla-puestos { width: 100%; border-collapse: collapse; font-size: 13px; }
.tabla-puestos th,
.tabla-puestos td {
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 6px;
  text-align: left;
  vertical-align: top;
}
.tabla-puestos th { background-color: #f4f5f7; font-weight: bold; }
.tabla-puestos .col-centro { text-align: center; }
.tabla-puestos .col-codigo { font-family: monospace; }
.tabla-puestos .sin-abreviatura { font-family: inherit; font-style: italic; color: #b26a00; }
.tabla-puestos .col-acciones a { margin-right: 8px; white-space: nowrap; }
.tabla-puestos tr.inactivo td { color: #9aa1a8; font-style: italic; }
.tabla-puestos .vacio { text-align: center; color: #8e8e93; padding: 16px; }
.etiqueta-estado {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  background-color: #8e8e93;
}
.estado-activo { background-color: #4cd964; }
.estado-inactivo { background-color: #8e8e93; }
</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import RhPuestos from "./../../js/db/RhPuestos.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import { esperaDelCarril } from "./../../js/utils/esperaGlobal.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

/**
 * Ajustes · Puestos de trabajo (#50). El catálogo de puestos del organigrama: alta en línea
 * encima de la tabla, y en cada fila editar y desactivar.
 *
 * No hay borrado, y no es un olvido: un puesto con funciones o personas colgando se desactiva y
 * se conserva en los históricos (trazabilidad 17020). Lo que se desactiva deja de ofrecerse en
 * el desplegable «Puesto de la función» y sigue aquí, con su estado.
 *
 * El catálogo entero llega en una petición y se pinta de una vez: son unas pocas decenas de
 * puestos, como mucho.
 */
export default {
  components: {
    rForm,
    rInput
  },
  data: function() {
    return {
      puestos: [],
      editando: this.enBlanco(),
      formKey: 0,
      error403: false
    };
  },
  created: function() {
    // La ruta lleva `keepAlive`, así que salir no destruye la pantalla: se sale, se vuelve y la
    // carga anterior sigue viva. El carril hace que solo conteste la última (ver
    // carrilPeticiones.js). Fuera de `data` porque no es estado que se pinte.
    //
    // #472 · Y el preloader lo enciende y lo apaga el carril: colgado del `then` de cada carga, la
    // respuesta descartada de una anterior lo apagaba con la vigente todavía en vuelo. Va por un
    // testigo de `esperaGlobal`, igual que las escrituras de esta pantalla, para que el `then` de
    // un guardado —que recarga— no le quite el indicador a esa recarga.
    this.carril = carrilPeticiones({ cargando: esperaDelCarril(this) });
  },
  methods: {
    enBlanco: function() {
      return { id: null, codigo: null, abreviatura: null, nombre: null };
    },
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    nuevo: function() {
      this.formKey++;
      this.editando = this.enBlanco();
    },
    /**
     * Entrar en la pantalla. No es `cargar` directamente porque con `keepAlive` el formulario
     * sobrevive a salir: se volvía con el puesto de antes a medio editar y el código bloqueado.
     * El botón «Recargar» sí llama solo a `cargar`, que ahí vaciar lo escrito sería el fallo.
     */
    entrar: function() {
      this.nuevo();
      this.cargar();
    },
    editar: function(puesto) {
      this.formKey++;
      this.editando = {
        id: puesto.id,
        codigo: puesto.codigo,
        abreviatura: puesto.abreviatura,
        nombre: puesto.nombre
      };
    },
    guardar: function() {
      const self = this;
      const app = self.$f7;
      const datos = self.$refs.formPuesto ? self.$refs.formPuesto.formData : {};
      // La abreviatura se exige en el alta y no en la edición, igual que el backend: los puestos
      // que ya existían pueden no tenerla, y bloquear su edición impediría corregirles el nombre.
      if (!datos.codigo || !datos.codigo.trim() || !datos.nombre || !datos.nombre.trim()
          || (!self.editando.id && (!datos.abreviatura || !datos.abreviatura.trim()))) {
        app.dialog.alert(self.$t('puestostrabajo.faltanDatos'));
        return;
      }
      const cuerpo = { codigo: datos.codigo, abreviatura: datos.abreviatura, nombre: datos.nombre };
      // #663 · La espera de la sesión, y no un testigo a pelo: si la sesión cambia en vuelo, la
      // frontera calla el `then` que la soltaba, y el testigo se quedaba en la cuenta común con el
      // indicador puesto para quien entrase. Esta se suelta también al cerrar la sesión.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      const peticion = self.editando.id
        ? RhPuestos.editar(self.editando.id, cuerpo)
        : RhPuestos.crear(cuerpo);
      peticion
        .then(function() {
          app.toast.create({ text: self.$t('puestostrabajo.guardado'), position: 'center', closeTimeout: 2500 }).open();
          self.nuevo();
          self.cargar();
        })
        .catch(function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('puestostrabajo.errorGuardando'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    cambiarActivo: function(puesto, activo) {
      const self = this;
      const app = self.$f7;
      // Desactivar no borra nada, pero saca el puesto de los desplegables: se confirma para que
      // no se haga de un clic despistado desde la fila.
      if (!activo) {
        app.dialog.confirm(self.$t('puestostrabajo.confirmaDesactivar'), self.$t('puestostrabajo.titulo'),
          function() { self.enviarActivo(puesto, false); });
        return;
      }
      self.enviarActivo(puesto, true);
    },
    enviarActivo: function(puesto, activo) {
      const self = this;
      const app = self.$f7;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhPuestos.cambiarActivo(puesto.id, activo, null)
        .then(function() {
          self.cargar();
        })
        .catch(function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('puestostrabajo.errorEstado'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    cargar: function() {
      const self = this;
      const app = self.$f7;
      // Sin filtro: el listado de Ajustes enseña también los desactivados, con su estado. El
      // `soloActivos` es para el desplegable de Funciones.
      self.carril.lanzar(RhPuestos.listar(false), {
        exito: function(res) {
          // Una carga que va bien manda sobre el 403 de cualquier intento anterior: con
          // `keepAlive` la pantalla no se recrea, así que el «No autorizado» se quedaba puesto
          // para siempre aunque «Recargar» trajera ya los datos (revisión CODEX del PR #1731).
          self.error403 = false;
          self.puestos = res || [];
        },
        // Guardar, desactivar y reactivar acaban llamando aquí: si falla esta carga, lo que queda
        // en pantalla es el catálogo de ANTES de la acción, con la fila recién desactivada
        // enseñándose activa. Se vacía y se dice que no se pudo cargar.
        limpiar: function() {
          self.puestos = [];
        },
        fallo: function(e) {
          if (self.es403(e)) self.error403 = true;
          else app.dialog.alert(e && e.message ? e.message : self.$t('puestostrabajo.errorCargando'));
        }
      });
    }
  }
};
</script>
